import { Dispatch, FC, Fragment, useEffect, useState } from "react";
import {
  Box, Button, Divider, FormControl, Grid, InputLabel,
  LinearProgress, Select, Stack, TextField, Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import { useStyles } from "../styles";
import CheckAttr from "../../../utils/CheckAttr";
import { DataRef } from "../../../types/DataRef";
import { AxiosInstance } from "axios";
import { CodeGesco, DataOperation } from "../../content/parametrage/Operation/Operation";
import { AlertType } from "../../../types/AlertType";
import { IPays } from "../../content/parametrage/Pays/Pays";
import { articlesSansActes } from "../../../constants/Constants";

interface CoutArticleAuto {
  libelle: string;
  coutactivite: string;
}

interface SocifactCodeGesco {
  index?: number;
  id?: number;
  societeFacturation?: number;
  codeGesco?: number;
  periode_facturations_id?: number;
  dataCodeGescos?: CodeGesco[];
  hasArticles?: boolean;
}

interface OperationToSave {
  id?: number;
  client?: number;
  operation: string;
  secteur_activite: number[];
  // Utilisés à la création pour les articles par défaut
  pays_id?: number | string;
  bu_id?: number | string;
  type_id?: number | string;
  // Coût/h des articles créés avec l'opération (date début = date de création)
  couts_auto?: CoutArticleAuto[];
  socifactCodeGescos: SocifactCodeGesco[];
  addedRows?: SocifactCodeGesco[];
  deletedRows: Number[];
}

interface OperationFormProps {
  assoc?: number | undefined;
  defaultClient?: DataRef;
  dataClients: DataRef[];
  dataSocieteFacturations: DataRef[];
  dataSecteurActivites: DataRef[];
  operation?: DataOperation;
  http: AxiosInstance;
  setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
  setAlerteMessage: Dispatch<React.SetStateAction<string>>;
  setAlertType: Dispatch<React.SetStateAction<AlertType>>;
  reloadList: () => void;
  onClose: () => void;
  periodeRefAll: DataRef[];
}

interface InputValidation {
  client: boolean;
  operation: boolean;
  pays: boolean;
  bu: boolean;
  type: boolean;
  // secteur_activite: boolean;
}

const initialValidation: InputValidation = {
  client: false,
  operation: false,
  pays: false,
  bu: false,
  type: false,
  // secteur_activite: false
};

const OperationForm: FC<OperationFormProps> = ({
  assoc,
  defaultClient,
  dataClients,
  dataSocieteFacturations,
  dataSecteurActivites,
  operation,
  http,
  setOpenSnack,
  setAlerteMessage,
  setAlertType,
  reloadList,
  onClose,
  periodeRefAll,
}) => {
  const classes = useStyles();

  const initialRow: SocifactCodeGesco = {
    index: 0,
    societeFacturation: undefined,
    codeGesco: undefined,
    hasArticles: false,
    dataCodeGescos: [],
    periode_facturations_id: undefined,
  };

  const initialData: OperationToSave = {
    client: undefined,
    operation: "",
    secteur_activite: [],
    pays_id: "",
    bu_id: "",
    type_id: "",
    couts_auto: articlesSansActes.map((libelle) => ({ libelle, coutactivite: "" })),
    socifactCodeGescos: [initialRow],
    addedRows: [],
    deletedRows: [],
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isClickSave, setIsClickSave] = useState<boolean>(false);

  const [operationToSave, setOperationToSave] = useState<OperationToSave>(initialData);

  const [errorInput, setErrorInput] = useState<InputValidation>(initialValidation);
  const [existCoupleRow, setExistCoupleRow] = useState<number | null>(null);

  const [indexNotValidate, setIndexNotValidate] = useState<number | undefined>(undefined);

  const [dataPaysBu, setDataPaysBu] = useState<IPays[]>([]);
  const [dataTypes, setDataTypes] = useState<DataRef[]>([]);

  const getDataRefCreation = () => {
    // Pays (avec BU) et Types pour les articles créés avec l'opération
    http
      .get(`allpaysbu`)
      .then((res) => {
        Array.isArray(res.data) && setDataPaysBu(res.data);
      })
      .catch((error) => {
        console.log(error);
      });

    http
      .get(`getalltype`)
      .then((res) => {
        Array.isArray(res.data) && setDataTypes(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const setDefaultOperationToSave = async () => {
    if (operation) {
      let tabRows: SocifactCodeGesco[] = [];
      let idToDelete: Number[] = [];

      for (
        let index = 0;
        index < operation?.pivot_oper_socifact_code_gesco?.length;
        index++
      ) {
        const mainAssociation = operation?.pivot_oper_socifact_code_gesco[index];
        const sociFact = mainAssociation?.piv_socifact_code_gescos?.societe_facturation?.id;
        const codeGesco = mainAssociation?.piv_socifact_code_gescos?.code_gesco?.id;
        const periode = mainAssociation?.periode_facturations_id;
        const id = mainAssociation?.piv_socifact_code_gescos?.id;
        mainAssociation?.articles?.length === 0 && idToDelete.push(mainAssociation?.id);


        await http
          .get(`code-gesco/findby/societe-facturation/${sociFact}`)
          .then((res) => {
            const dataCodeGescos = res.data as CodeGesco[];

            const rowToAdd: SocifactCodeGesco = {
              index: index,
              id: id,
              societeFacturation: sociFact,
              codeGesco: codeGesco,
              hasArticles: mainAssociation?.articles?.length > 0 && true,
              dataCodeGescos: dataCodeGescos,
              periode_facturations_id: periode,
            };
            tabRows.push(rowToAdd);
          })
          .catch((error) => {
            console.log(error);
          });
      }

      const defaultOperation: OperationToSave = {
        id: operation?.id,
        client: operation?.client?.id,
        operation: operation?.libelle,
        secteur_activite: operation?.secteur_activites?.map(secteur => secteur?.id),
        socifactCodeGescos: tabRows,
        addedRows: [],
        deletedRows: idToDelete,
      };

      setOperationToSave(defaultOperation);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (defaultClient) {
      setOperationToSave({
        ...operationToSave,
        client: defaultClient?.id
      });
    }

    if (!operation) {
      getDataRefCreation();
      setIsLoading(false);
    }

    setDefaultOperationToSave();

    return () => {
      setOperationToSave(initialData);
    };
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'secteur_activite') {
      let secteurValue = operationToSave?.secteur_activite;
      secteurValue[0] = value;

      setOperationToSave({
        ...operationToSave,
        secteur_activite: secteurValue,
      });
    } else if (name === 'pays_id') {
      // Changement de pays : on réinitialise la BU
      setOperationToSave({
        ...operationToSave,
        pays_id: value,
        bu_id: "",
      });
    } else {
      setOperationToSave({
        ...operationToSave,
        [name]: value,
      });
    }
  };

  const handleChangeCoutAuto = (index: number, value: string) => {
    const couts = [...(operationToSave?.couts_auto || [])];
    couts[index] = { ...couts[index], coutactivite: value };

    setOperationToSave({
      ...operationToSave,
      couts_auto: couts,
    });
  };

  const handleChangeRow = (index: number, e: any) => {
    const { name, value } = e.target;
    const values = [...operationToSave.socifactCodeGescos];

    if (name === "societeFacturation") {
      values[index].societeFacturation = value;

      if (value) {
        // Recupère tous les codes gescos
        http
          .get(`code-gesco/findby/societe-facturation/${value}`)
          .then((res) => {
            const dataCodeGescos = res.data as CodeGesco[];
            values[index].dataCodeGescos = dataCodeGescos;

            setOperationToSave({
              ...operationToSave,
              socifactCodeGescos: values,
            });
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        values[index].dataCodeGescos = [];
      }
    }

    if (name === "codeGesco") {
      values[index].codeGesco = value;

      if (value) {
        // Recupère l'id du couple societe de facturation - code gesco
        http
          .get(
            `pivot/socifact-codegesco/findby/societe/code/${values[index]?.societeFacturation}/${value}`
          )
          .then((res) => {
            const { id } = res.data[0];

            values[index].id = id;

            setOperationToSave({
              ...operationToSave,
              socifactCodeGescos: values,
            });
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }

    if (name === "periode") {
      values[index].periode_facturations_id = value;
      setOperationToSave({
        ...operationToSave,
        socifactCodeGescos: values,
      });
    }
  };

  const isValidate = (): boolean => {
    // Reinitialise les variables de validation
    setErrorInput(initialValidation);

    // Pays, BU et Type ne sont demandés qu'à la création
    const isCreation = !operation;

    if (
      !CheckAttr(operationToSave?.client) ||
      !CheckAttr(operationToSave?.operation) ||
      (isCreation && (
        !CheckAttr(operationToSave?.pays_id) ||
        !CheckAttr(operationToSave?.bu_id) ||
        !CheckAttr(operationToSave?.type_id)
      ))
      // !CheckAttr(operationToSave?.secteur_activite)
    ) {
      if (!CheckAttr(operationToSave?.client)) {
        setErrorInput((prev: InputValidation) => ({ ...prev, client: true }));
      }

      if (!CheckAttr(operationToSave?.operation)) {
        setErrorInput((prev: InputValidation) => ({ ...prev, operation: true }));
      }

      if (isCreation && !CheckAttr(operationToSave?.pays_id)) {
        setErrorInput((prev: InputValidation) => ({ ...prev, pays: true }));
      }

      if (isCreation && !CheckAttr(operationToSave?.bu_id)) {
        setErrorInput((prev: InputValidation) => ({ ...prev, bu: true }));
      }

      if (isCreation && !CheckAttr(operationToSave?.type_id)) {
        setErrorInput((prev: InputValidation) => ({ ...prev, type: true }));
      }

      /* if (!CheckAttr(operationToSave?.secteur_activite)) {
        setErrorInput((prev: InputValidation) => ({ ...prev, secteur_activite: true }));
      } */

      setAlerteMessage("Merci de remplir les champs en rouge SVP!");
      setAlertType("warning");
      setOpenSnack(true);

      return false;
    }
    return true;
  };

  const cleanData = () => {
    let finalData: OperationToSave | boolean = false;
    const tabCouples: SocifactCodeGesco[] = [];

    let indiceNotValidate: number | undefined = undefined;

    for (const row of operationToSave?.socifactCodeGescos) {
      if (CheckAttr(row?.societeFacturation) && CheckAttr(row?.codeGesco)) {
        if (CheckAttr(row?.periode_facturations_id)) {
          tabCouples.push({
            id: row?.id,
            societeFacturation: row?.societeFacturation,
            codeGesco: row?.codeGesco,
            dataCodeGescos: row?.dataCodeGescos,
            periode_facturations_id: row?.periode_facturations_id,
          });
        } else {
          indiceNotValidate = row?.index;
          setAlerteMessage("Merci de renseigner le champ période de facturation en rouge SVP !");
          setAlertType("warning");
          setOpenSnack(true);
          break;
        }
      }
    }

    if (indiceNotValidate === undefined) {
      if (tabCouples.length > 0) {
        finalData = {
          id: operationToSave?.id,
          client: operationToSave?.client,
          operation: operationToSave?.operation,
          secteur_activite: operationToSave?.secteur_activite,
          pays_id: operationToSave?.pays_id,
          bu_id: operationToSave?.bu_id,
          type_id: operationToSave?.type_id,
          couts_auto: operationToSave?.couts_auto?.filter((cout) => CheckAttr(cout?.coutactivite)),
          socifactCodeGescos: tabCouples,
          deletedRows: operationToSave?.deletedRows,
        };
      } else {
        setAlerteMessage("Merci d' ajouter au moins un couple Societe de facturation - Code gesco SVP !");
        setAlertType("warning");
        setOpenSnack(true);
      }
    } else {
      setIndexNotValidate(indiceNotValidate);
    }

    return finalData;
  };

  const handleSave = () => {
    setExistCoupleRow(null);
    setIndexNotValidate(undefined);
    const datas = cleanData();

    if (isValidate() && datas) {
      if (operation) {
        let IsDuplicate = false;

        datas?.socifactCodeGescos?.forEach((societeCode, key) => {
          let isAlreadyAdded = datas?.socifactCodeGescos?.filter(
            (item) =>
              (item.codeGesco as Number) == (societeCode.codeGesco as Number) &&
              (item.societeFacturation as Number) ==
              (societeCode.societeFacturation as Number)
          );
          if (isAlreadyAdded?.length >= 2) {
            IsDuplicate = true;
            const libelleSociFact = dataSocieteFacturations?.find(
              (elem) =>
                Number(elem?.id) ===
                Number(isAlreadyAdded[0].societeFacturation)
            )?.libelle;

            const libelleCodeGesco = isAlreadyAdded[0].dataCodeGescos?.find(
              (gesco) => gesco.id == Number(isAlreadyAdded[0].codeGesco)
            )?.libelle;

            const message = `Le couple ${libelleSociFact} - ${libelleCodeGesco} - ${operationToSave?.operation} éxiste déjà !`;
            setAlerteMessage(message);
            setAlertType("warning");
            setOpenSnack(true);
          }
        });

        if (!IsDuplicate) {
          setIsClickSave(true);
          http.put(`operation/update/${datas?.id}`, datas)
            .then((res) => {
              /* if (res.data === 1) {
                setAlerteMessage("Cette Operation Existe Deja");
                setAlertType("warning");
                setOpenSnack(true);
              } else {
                setAlerteMessage("Enregistrement efféctuée !");
                setAlertType("success");
                setOpenSnack(true);
                onClose();
                reloadList();
              } */
              setAlerteMessage("Enregistrement efféctuée !");
              setAlertType("success");
              setOpenSnack(true);
              onClose();
              reloadList();
            }).catch((error) => {
              console.log(error);
              setAlertType("error");
              setOpenSnack(true);
            }).finally(() => {
              setIsClickSave(false);
            });
        }
      } else {
        // Verification unicité
        http
          .post("operation/unicity-check/for-add", datas)
          .then((res) => {
            if (res.data?.result?.exist) {
              // Cas couple qui existe déja
              const index = res.data?.result?.index;

              const idSociFact = datas?.socifactCodeGescos[index]
                .societeFacturation as number;
              const idSociCodeGesco = datas?.socifactCodeGescos[index]
                .codeGesco as number;

              const libelleSociFact = dataSocieteFacturations?.find(
                (elem) => Number(elem?.id) === Number(idSociFact)
              )?.libelle;
              const libelleCodeGesco = datas?.socifactCodeGescos[
                Number(index)
              ].dataCodeGescos?.find(
                (elem) => Number(elem?.id) === Number(idSociCodeGesco)
              )?.libelle;

              // const message = `Le couple ${libelleSociFact} - ${libelleCodeGesco} - ${operationToSave?.operation} éxiste déjà !`;
              libelleCodeGesco == undefined
                ? setAlerteMessage(
                  `Le couple ${libelleSociFact} - ${operationToSave?.operation} éxiste déjà !`
                )
                : setAlerteMessage(
                  `Le couple ${libelleSociFact} - ${libelleCodeGesco} - ${operationToSave?.operation} éxiste déjà !`
                );

              // setAlerteMessage(message);
              setAlertType("warning");
              setOpenSnack(true);
              setExistCoupleRow(index);
              setIsClickSave(false);
            } else {
              // Create
              setIsClickSave(true);
              http.post("operation/create", datas)
                .then((res) => {
                  if (res.data === 1) {
                    setAlerteMessage("Cette Operation Existe Deja");
                    setAlertType("warning");
                    setOpenSnack(true);
                  } else {
                    setAlerteMessage("Enregistrement efféctuée !");
                    setAlertType("success");
                    setOpenSnack(true);
                    onClose();
                    reloadList();
                  }
                }).catch((error) => {
                  console.log(error);
                  setAlertType("error");
                  setOpenSnack(true);
                }).finally(() => {
                  setIsClickSave(false);
                });
            }
          })
          .catch((error) => {
            console.log(error);
            setAlertType("error");
            setOpenSnack(true);
          });
      }
    }
  };

  const handleAddRow = () => {
    const tmpSocifactGesco = operationToSave.socifactCodeGescos;

    (tmpSocifactGesco.length === indexNotValidate) && setIndexNotValidate(undefined);

    tmpSocifactGesco.push({
      index: tmpSocifactGesco.length,
      societeFacturation: undefined,
      codeGesco: undefined,
      dataCodeGescos: [],
    });

    setOperationToSave({
      ...operationToSave,
      socifactCodeGescos: tmpSocifactGesco,
    });
  };

  const handleRemoveRow = (index: any) => {
    const tempArray = [...operationToSave.socifactCodeGescos];
    const deletedRow = tempArray[index]?.id!;
    tempArray.splice(index, 1);

    const initDeletedRows = operationToSave?.deletedRows;
    initDeletedRows.push(deletedRow);

    setOperationToSave({
      ...operationToSave,
      socifactCodeGescos: tempArray,
      deletedRows: initDeletedRows,
    });
  };

  let defaultDisableValue = false;
  if (assoc || defaultClient) {
    defaultDisableValue = true;
  }

  const defaultIndex = !operation ? 0 : null;

  // Date début des coûts des articles auto = date de création de l'opération (date locale)
  const today = new Date();
  const dateCreation = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  /**
   * Logique page loading...........................
   */
  const progress = (
    <div className={classes.loading}>
      <LinearProgress color="error" sx={{ my: 2 }} />
      <LinearProgress color="warning" />
    </div>
  );

  const secteurActiviteField = (
      <FormControl
        variant="outlined"
        className={classes.formControl}
        sx={{ marginLeft: 1, marginTop: 1 }}
      >
        <InputLabel
          htmlFor="secteur_activite"
          color="warning"
          shrink={CheckAttr(operationToSave?.secteur_activite)}
        >
          Secteur d’activité
        </InputLabel>
        <Select
          id="secteur_activite"
          native
          color="warning"
          value={operationToSave?.secteur_activite[0]}
          // error={errorInput?.secteur_activite}
          onChange={handleChange}
          label="Secteur d’activité"
          inputProps={{
            name: "secteur_activite",
          }}
        >
          <option value=""></option>
          {dataSecteurActivites.map((secteur) => (
            <option key={secteur?.id} value={secteur?.id}>
              {secteur?.libelle}
            </option>
          ))}
        </Select>
      </FormControl>
  );

  // Pays, BU et Type des articles créés avec l'opération
  const paysField = (
      <FormControl
        variant="outlined"
        className={classes.formControl}
        sx={{ marginLeft: 1, marginTop: 1 }}
      >
        <InputLabel
          htmlFor="pays_id"
          color="warning"
          shrink={CheckAttr(operationToSave?.pays_id)}
        >
          Pays
        </InputLabel>
        <Select
          id="pays_id"
          native
          color="warning"
          value={operationToSave?.pays_id}
          error={errorInput?.pays}
          onChange={handleChange}
          label="Pays"
          inputProps={{
            name: "pays_id",
          }}
        >
          <option value=""></option>
          {dataPaysBu.map((pays) => (
            <option key={pays?.id} value={pays?.id}>
              {pays?.libelle}
            </option>
          ))}
        </Select>
      </FormControl>
  );

  const buField = (
      <FormControl
        variant="outlined"
        className={classes.formControl}
        sx={{ marginLeft: 1, marginTop: 1 }}
      >
        <InputLabel
          htmlFor="bu_id"
          color="warning"
          shrink={CheckAttr(operationToSave?.bu_id)}
        >
          BU
        </InputLabel>
        <Select
          id="bu_id"
          native
          color="warning"
          value={operationToSave?.bu_id}
          error={errorInput?.bu}
          onChange={handleChange}
          label="BU"
          inputProps={{
            name: "bu_id",
          }}
        >
          <option value=""></option>
          {(dataPaysBu.find((pays) => Number(pays?.id) === Number(operationToSave?.pays_id))?.bus || []).map((bu) => (
            <option key={bu?.id} value={bu?.id}>
              {bu?.libelle}
            </option>
          ))}
        </Select>
      </FormControl>
  );

  const typeField = (
      <FormControl
        variant="outlined"
        className={classes.formControl}
        sx={{ marginLeft: 1, marginTop: 1 }}
      >
        <InputLabel
          htmlFor="type_id"
          color="warning"
          shrink={CheckAttr(operationToSave?.type_id)}
        >
          Type
        </InputLabel>
        <Select
          id="type_id"
          native
          color="warning"
          value={operationToSave?.type_id}
          error={errorInput?.type}
          onChange={handleChange}
          label="Type"
          inputProps={{
            name: "type_id",
          }}
        >
          <option value=""></option>
          {dataTypes.map((type) => (
            <option key={type?.id} value={type?.id}>
              {type?.libelle}
            </option>
          ))}
        </Select>
      </FormControl>
  );

  const formContent = (
    <Fragment>
      <Box className={classes.root}>
        <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
          <Grid item xs={4} sm={4} md={4}>
            <FormControl
              variant="outlined"
              className={classes.formControl}
              sx={{ marginLeft: 1, marginTop: 1 }}
            >
              <InputLabel
                htmlFor="client"
                color="warning"
                shrink={CheckAttr(operationToSave?.client)}
              >
                Client
              </InputLabel>
              <Select
                id="client"
                native
                color="warning"
                disabled={defaultDisableValue}
                value={operationToSave?.client}
                error={errorInput?.client}
                onChange={handleChange}
                label="Client"
                inputProps={{
                  name: "client",
                }}
              >
                <option value=""></option>
                {dataClients.map((client) => (
                  <option key={client?.id} value={client?.id}>
                    {client?.libelle}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4} sm={4} md={4}>
            <TextField
              name="operation"
              label="Opération"
              color="warning"
              autoComplete="off"
              value={operationToSave?.operation}
              error={errorInput?.operation}
              onChange={handleChange}
              variant="outlined"
              InputLabelProps={{
                shrink: CheckAttr(operationToSave?.operation),
              }}
            />
          </Grid>

          <Grid item xs={4} sm={4} md={4}>
            {secteurActiviteField}
          </Grid>
        </Grid>

        <Box ml={2} mt={4} mb={1}>
          <Typography variant="h6">Couple société de facturation – Code Gesco</Typography>
          <Divider />
        </Box>
        <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
          {operationToSave?.socifactCodeGescos.map((row, index) => (
            <Fragment key={index}>
              <Grid item xs={6} sm={4} md={4}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  sx={{ marginLeft: 1, marginTop: 1 }}
                >
                  <InputLabel
                    htmlFor="societeFacturation"
                    color="warning"
                    shrink={CheckAttr(row?.societeFacturation)}
                  >
                    Société de facturation
                  </InputLabel>
                  <Select
                    id="societeFacturation"
                    native
                    color="warning"
                    disabled={row?.hasArticles}
                    error={existCoupleRow === index && true}
                    value={row?.societeFacturation}
                    onChange={(e) => handleChangeRow(index, e)}
                    label="Société de facturation"
                    inputProps={{
                      name: "societeFacturation",
                    }}
                  >
                    <option value=""></option>
                    {dataSocieteFacturations.map((societe) => (
                      <option key={societe?.id} value={societe?.id}>
                        {societe?.libelle}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={4}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  sx={{ marginLeft: 1, marginTop: 1 }}
                >
                  <InputLabel
                    htmlFor="codeGesco"
                    color="warning"
                    shrink={CheckAttr(row?.codeGesco)}
                  >
                    Code gesco
                  </InputLabel>
                  <Select
                    id="codeGesco"
                    native
                    color="warning"
                    disabled={row?.hasArticles}
                    error={existCoupleRow === index && true}
                    value={row?.codeGesco}
                    onChange={(e) => handleChangeRow(index, e)}
                    label="Code gesco"
                    inputProps={{
                      name: "codeGesco",
                    }}
                  >
                    <option value=""></option>
                    {(
                      operationToSave?.socifactCodeGescos[index]
                        .dataCodeGescos || []
                    ).map((codeGesco) => (
                      <option key={codeGesco?.id} value={codeGesco?.id}>
                        {codeGesco?.libelle} - {codeGesco?.intituleTiers}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={4}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  sx={{ marginLeft: 1, marginTop: 1 }}
                >
                  <InputLabel
                    htmlFor="codeGesco"
                    color="warning"
                    shrink={CheckAttr(row?.periode_facturations_id)}
                  >
                    Periode de Facturation
                  </InputLabel>
                  <Select
                    id="codeGesco"
                    native
                    color="warning"
                    error={[existCoupleRow, indexNotValidate]?.includes(index)}
                    value={row?.periode_facturations_id}
                    onChange={(e) => handleChangeRow(index, e)}
                    label="periode"
                    inputProps={{
                      name: "periode",
                    }}
                  >
                    <option value=""></option>
                    {periodeRefAll.map((periode) => (
                      <option key={periode?.id} value={periode?.id}>
                        {periode?.libelle}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={12} md={12}>
                {index != defaultIndex && (
                  <Box display="flex" justifyContent="end" alignItems="start">
                    <Button
                      variant="contained"
                      size="small"
                      color="inherit"
                      disabled={row?.hasArticles}
                      onClick={() => handleRemoveRow(index)}
                    >
                      <ClearIcon
                        sx={{
                          fontSize: 26,
                          color: row?.hasArticles ? "#D6D6DA" : "#000",
                        }}
                      />
                    </Button>
                  </Box>
                )}
              </Grid>
            </Fragment>
          ))}
          <Stack
            direction="row"
            spacing={2}
            display="flex"
            justifyContent="start"
            mt={2}
          >
            <Button
              variant="contained"
              color="inherit"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleAddRow()}
              style={{ textTransform: "none", color: "#080d24" }}
            >
              Ajouter Couple
            </Button>
          </Stack>
        </Grid>

        {/* Articles créés automatiquement avec l'opération : pays, BU, type et coût/h (secteur repris d'en haut) */}
        {!operation && (
          <Fragment>
            <Box ml={2} mt={4} mb={1}>
              <Typography variant="h6">Articles créés automatiquement</Typography>
              <Divider />
            </Box>
            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
              <Grid item xs={6} sm={4} md={4}>
                {paysField}
              </Grid>
              <Grid item xs={6} sm={4} md={4}>
                {buField}
              </Grid>
              <Grid item xs={6} sm={4} md={4}>
                {typeField}
              </Grid>
            </Grid>
            <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }} mt={0}>
              {(operationToSave?.couts_auto || []).map((cout, index) => (
                <Fragment key={cout.libelle}>
                  <Grid item xs={6} sm={6} md={6}>
                    <TextField
                      label="Article"
                      color="warning"
                      value={cout.libelle}
                      variant="outlined"
                      disabled
                      fullWidth
                      sx={{ marginLeft: 1, marginTop: 1 }}
                    />
                  </Grid>
                  <Grid item xs={3} sm={3} md={3}>
                    <TextField
                      label="Coût/h"
                      color="warning"
                      type="number"
                      autoComplete="off"
                      value={cout.coutactivite}
                      onChange={(e) => handleChangeCoutAuto(index, e.target.value)}
                      variant="outlined"
                      fullWidth
                      sx={{ marginTop: 1 }}
                    />
                  </Grid>
                  <Grid item xs={3} sm={3} md={3}>
                    <TextField
                      label="Date début"
                      color="warning"
                      type="date"
                      value={dateCreation}
                      variant="outlined"
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ marginTop: 1 }}
                    />
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Fragment>
        )}
      </Box>
      <Box display="flex" justifyContent="center" mt={5}>
        <LoadingButton
          onClick={() => handleSave()}
          endIcon={<SaveIcon />}
          loading={isClickSave}
          loadingPosition="end"
          sx={{ textTransform: "none" }}
          variant="contained"
          color="warning"
        >
          Enregistrer
        </LoadingButton>
      </Box>
    </Fragment>
  );

  const loader = isLoading ? progress : formContent;

  return loader;
};

export default OperationForm;
